import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook that triggers a visibility state when the element scrolls into view.
 * Once visible, the element stays visible (one-shot).
 */
const useScrollReveal = (options = {}) => {
  const { threshold = 0.15, rootMargin = '0px 0px -40px 0px' } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const current = ref.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(current);
    return () => observer.unobserve(current);
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

/**
 * Hook for staggered reveal of multiple children.
 * Returns a ref for the container and a function to get classes for each child index.
 */
export const useStaggerReveal = (itemCount, options = {}) => {
  const { threshold = 0.1, staggerDelay = 0.1 } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const current = ref.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(current);
    return () => observer.unobserve(current);
  }, [threshold]);

  const getItemProps = useCallback(
    (index) => ({
      className: `transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`,
      style: { transitionDelay: isVisible ? `${index * staggerDelay}s` : '0s' },
    }),
    [isVisible, staggerDelay]
  );

  return [ref, isVisible, getItemProps];
};

export default useScrollReveal;
